import React, { useState } from 'react';
import { captureException } from '@nais/apm';
import { postFeilOgMangler } from 'clients/apiClient';
import { ErrorResponse } from 'types/errors';
import Header from 'components/header/Header';
import { vars } from 'src/Config';
import { paths } from 'common/paths';
import { FormattedMessage, useIntl } from 'react-intl';
import Takk from 'components/takk/Takk';
import FeilgOgManglerOnskerAaKontaktes from './FeilOgManglerOnskerAaKontaktes';
import { triggerHotjar } from 'utils/hotjar';
import { MetaTags } from 'components/metatags/MetaTags';
import { Alert, Box, Button, GuidePanel, Radio, RadioGroup, Textarea } from '@navikt/ds-react';
import { Controller, FieldValues, FormProvider, useForm } from 'react-hook-form';
import { resolveErrorCode } from '../../../utils/errorCodes';
import appStyle from 'src/App.module.scss';
import { FEIL_OG_MANGLER_TYPE } from 'common/types/FeilOgMangler';

export interface FeilOgManglerFields {
	onskerKontakt: boolean;
	epost: string;
	feiltype: FEIL_OG_MANGLER_TYPE;
	melding: string;
}

const FOM = () => {
	const methods = useForm<FeilOgManglerFields>({
		reValidateMode: 'onChange',
	});

	const {
		register,
		handleSubmit,
		watch,
		control,
		formState: { errors, isValid, isSubmitted },
	} = methods;

	const [loading, setLoading] = useState(false);
	const [success, setSuccess] = useState(false);
	const [error, setError] = useState<ErrorResponse>();
	const { formatMessage } = useIntl();

	const send = (values: FieldValues) => {
		const { onskerKontakt, feiltype, melding, epost } = values;

		const outbound = {
			feiltype,
			onskerKontakt,
			...(onskerKontakt && {
				epost,
			}),
			melding,
		};

		setLoading(true);
		postFeilOgMangler(outbound)
			.then(() => {
				setSuccess(true);
				triggerHotjar('feilogmangler');
			})
			.catch((error: ErrorResponse) => {
				setError(error);
				captureException(error, {
					fingerprint: 'feil-og-mangler.post-feil-og-mangler',
					context: {
						component: 'FeilOgMangler',
						action: 'postFeilOgMangler',
						errorCode: error?.errorCode,
					},
				});
			})
			.then(() => {
				setLoading(false);
			});
	};

	return (
		<div className={appStyle.pageContent}>
			<MetaTags
				titleId={'tilbakemeldinger.feil-og-mangler.sidetittel'}
				descriptionId={'seo.feil-og-mangler.description'}
				path={paths.tilbakemeldinger.feilogmangler}
			/>
			<Header
				title={formatMessage({
					id: 'tilbakemeldinger.feilogmangler.form.tittel',
				})}
			/>
			<GuidePanel poster className={appStyle.veileder}>
				<FormattedMessage id={'tilbakemeldinger.feilogmangler.form.veileder'} />
			</GuidePanel>
			<Box background="default" padding={{ xs: 'space-16', md: 'space-32' }}>
				{success ? (
					<Takk />
				) : (
					<FormProvider {...methods}>
						<form className={appStyle.skjema} onSubmit={handleSubmit(send)}>
							<Controller
								render={({ field, fieldState: { error } }) => (
									<RadioGroup
										{...field}
										legend={formatMessage({
											id: 'felter.typefeil.tittel',
										})}
										error={error?.message}
										value={field.value ?? null}
									>
										<Radio value={'TEKNISK_FEIL'}>
											{formatMessage({
												id: 'felter.typefeil.tekniskfeil',
											})}
										</Radio>
										<Radio value={'FEIL_INFO'}>
											{formatMessage({
												id: 'felter.typefeil.feilinformasjon',
											})}
										</Radio>
										<Radio value={'UNIVERSELL_UTFORMING'}>
											{formatMessage({
												id: 'felter.typefeil.uu',
											})}
										</Radio>
									</RadioGroup>
								)}
								control={control}
								name={'feiltype'}
								rules={{
									required: formatMessage({
										id: 'validering.feiltype.pakrevd',
									}),
								}}
							/>

							<div className={appStyle.skjemaInline}>
								<Textarea
									aria-required
									description={<FormattedMessage id={'felter.melding.beskrivelse'} />}
									{...register('melding', {
										required: formatMessage({
											id: 'validering.melding.pakrevd',
										}),
										maxLength: {
											value: vars.maksLengdeMelding,
											message: formatMessage({
												id: 'validering.melding.tegn',
											}),
										},
									})}
									label={formatMessage({
										id: 'felter.melding.tittel',
									})}
									value={watch().melding}
									error={errors?.melding?.message}
									maxLength={vars.maksLengdeMelding}
									autoComplete="off"
								/>
							</div>

							<FeilgOgManglerOnskerAaKontaktes />

							{error && (
								<Alert variant={'error'}>
									<FormattedMessage id={resolveErrorCode(error.errorCode)} />
								</Alert>
							)}
							<Button
								type={'submit'}
								variant={'primary'}
								disabled={loading || (isSubmitted && !isValid)}
								loading={loading}
							>
								<FormattedMessage id={'felter.send'} />
							</Button>
						</form>
					</FormProvider>
				)}
			</Box>
		</div>
	);
};
export default FOM;
